import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BookingStoreService } from 'avn/modules/booking-flow/booking-store.service';
import { COUNTRY_CODES, defaultCountryCode } from 'avn/services/country-codes';
import { ConfirmPopComponent } from 'avn/shared/components/confirm-pop/confirm-pop.component';
import * as moment from 'moment';
import * as momentz from 'moment-timezone';
import { BehaviorSubject, Subject } from 'rxjs';

import { SaveListComponent } from './components/save-list/save-list.component';
import { timezoneConstants, TimezoneInfo } from './timezone.constants';

export class ConfirmPopData {
  title = 'Confirm';
  message = 'Are you sure?';
  cancelButtonText = 'Cancel';
  okButtonText = 'Confirm';
  okClassName: 'btn-success' | 'btn-warning' | 'btn-error' = 'btn-success';
  secondaryButtons?: { name: string; class_name: string; value: string }[] = [];
  cancelType: 'button' | 'icon' = 'button';
}

@Injectable({
  providedIn: 'root'
})
export class SharedCompsService {
  constructor(
    private dialog: MatDialog,
    private booking_store: BookingStoreService,
    private router: Router
  ) {}

  private snackBarSubject = new BehaviorSubject<any>({});
  public snackBar = this.snackBarSubject.asObservable();

  snackBarClose = new Subject<boolean>();

  locationDropdownUpdate = new Subject<any>();

  pageChangeDetect = new Subject();

  currentTimezone = '';

  showSnackBar(message: string, duration = 5, buttonText = 'Dismiss', sendBack = false, routerLink?: any) {
    this.snackBarSubject.next({ message, duration, buttonText, sendBack, routerLink });
  }

  getTimezone() {
    if (!this.currentTimezone) {
      this.currentTimezone = localStorage.getItem('timezone') || 'Australia/Melbourne';
    }
    return this.currentTimezone;
  }

  convertedCurrentTimeWithTimezone(timezone?: string) {
    return momentz.tz(moment(), timezone || this.getTimezone());
  }

  confirmPop(options: ConfirmPopData) {
    const confirm = this.dialog.open(ConfirmPopComponent, {
      width: '400px',
      data: { ...options },
      disableClose: true,
      panelClass: ['confirm-pop-modal', 'popup-container']
    });

    return confirm;
  }

  openSaveListPop(options: any) {
    const confirm = this.dialog.open(SaveListComponent, {
      width: '90%',
      maxWidth: '500px',
      maxHeight: '70%',
      data: { ...options },
      disableClose: true,
      panelClass: ['save-list-pop-modal', 'popup-container']
    });

    return confirm;
  }

  fetchOnlyAustraliaTimezone() {
    let timezone = momentz.tz.guess();
    const index = timezoneConstants.timezones.findIndex((x: TimezoneInfo) => x.timezone === timezone);
    if (index === -1) {
      timezone = 'Australia/Melbourne';
    }

    return timezone;
  }

  // convertedCurrentTimeWithTimezone() {
  //   return momentz.tz(moment(), this.fetchOnlyAustraliaTimezone());
  // }

  getLocation(loc: any, format = '') {
    if (format) {
      const replacedFormat = format.replace(/{{\s*([^}]+)\s*}}/g, (_match, key: string) => {
        const value = key.split('.').reduce((acc: any, prop: string) => {
          if (acc === undefined || acc === null) {
            return undefined;
          }
          return acc[prop];
        }, loc);
        return value !== undefined && value !== null ? value : '';
      });

      return replacedFormat
        .replace(/\s+/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/,\s+/g, ', ')
        .replace(/^[\s,]+/, '')
        .replace(/[\s,]+$/, '')
        .trim();
    }
    const location = [];
    if (loc?.apartment) {
      location.push(loc?.apartment);
    }
    if (loc?.address) {
      location.push(loc?.address);
    }
    const city_name = loc?.city;
    const address = loc?.address;
    const post_code = loc?.postal_code;
    if ((city_name && !address) || (city_name && address && !address.toLowerCase().includes(city_name.toLowerCase()))) {
      location.push(city_name);
    }
    if (loc?.postal_code && !address.toLowerCase().includes(post_code.toLowerCase())) {
      location.push(loc?.postal_code);
    }
    return location.join(', ');
  }

  convertTime(current_time: string, extend: string, format: string) {
    let hours_to_extend = 0;
    let minutes_to_extend = 0;

    if (!isNaN(Number(extend))) {
      extend += ' mins';
    }

    const hours_to_check = extend.toLowerCase().split('hr');
    if (!isNaN(Number(hours_to_check[0]))) {
      hours_to_extend = Number(hours_to_check[0]);
      extend = hours_to_check[1];
    }
    const minutes_to_check = extend.toLowerCase().split('mins');
    if (!isNaN(Number(minutes_to_check[0]))) {
      minutes_to_extend = Number(minutes_to_check[0]);
    }

    return moment(current_time, 'hh:mm A')
      .add(minutes_to_extend, 'minutes')
      .add(hours_to_extend, 'hours')
      .format(format || 'HH:mm');
  }

  getCountryCodeFromPhone(phone: string) {
    const hasCode = phone?.includes('+');
    if (hasCode) {
      const list_filter = COUNTRY_CODES.filter((x: any) => phone.includes(x.code));
      return list_filter?.[0]?.code || defaultCountryCode;
    }
    return defaultCountryCode;
  }

  goToBooking(booking_slug: {
    business: string;
    service: string;
    prac?: string | number;
    date?: string;
    time?: string;
    goto?: string;
    return?: 'navigate' | 'return';
  }) {
    const url_split = this.router.url.split('?');
    sessionStorage.setItem('path-from', url_split[0]);
    if (url_split.length > 1) {
      sessionStorage.setItem('path-query', url_split[1]);
    }
    this.booking_store.resetBookingData();
    let url = `/booking/${booking_slug.business}/${booking_slug.service}`;
    if (booking_slug.prac) {
      url += `/${booking_slug.prac}`;
    }
    if (booking_slug.date) {
      url += `/${booking_slug.date}`;
    }
    if (booking_slug.time) {
      url += `/${booking_slug.time.replace(/ /g, '')}`;
    }
    if (booking_slug.goto) {
      url += `/${booking_slug.goto}`;
    }

    if (booking_slug.return === 'return') {
      return [url];
    } else {
      let queryParams: any = {};
      queryParams = this.parseQueryString(sessionStorage.getItem('path-query') || '');
      this.router.navigate([url], {
        queryParams
      });
      return;
    }
  }

  parseQueryString(query: string) {
    if (query.includes('?')) {
      query = query.split('?')[1];
    }
    const params = new URLSearchParams(query);
    const result: any = {};

    params.forEach((value, key) => {
      // Convert numeric strings to actual numbers if possible
      const numberValue = Number(value);
      result[key] = isNaN(numberValue) ? value : numberValue;
    });

    return result;
  }
}
